import { useEffect, useRef, useState } from "react";

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const stats = [
    { value: 0, label: "Founded", suffix: "", text: "2020" },
    { value: 500, label: "Clients Served", suffix: "+" },
    { value: 10000, label: "Placements Made", suffix: "+", format: true },
    { value: 0, label: "Coverage", suffix: "", text: "UK-Wide" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-gradient-stats py-12 text-primary-foreground">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map((stat, index) => (
            <StatItem 
              key={index} 
              stat={stat} 
              index={index} 
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface StatItemProps {
  stat: {
    value: number;
    label: string;
    suffix: string;
    format?: boolean;
    text?: string;
  };
  index: number;
  isVisible: boolean;
}

const StatItem = ({ stat, index, isVisible }: StatItemProps) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible || stat.text) return;

    const duration = 2000;
    const steps = 60;
    const stepValue = stat.value / steps;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setCount(Math.min(Math.floor(stepValue * currentStep), stat.value));
      } else {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isVisible, stat.value, stat.text]);

  const formatNumber = (num: number) => {
    if (stat.format) {
      return num.toLocaleString();
    }
    return num.toString();
  };

  return (
    <div 
      className="text-center opacity-0 translate-y-4 transition-all duration-700 ease-out"
      style={{
        animationDelay: `${index * 150}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(1rem)',
        transitionDelay: `${index * 150}ms`,
        willChange: 'transform, opacity',
        contain: 'layout'
      }}
    >
      <div className="mb-2 text-4xl font-bold md:text-5xl leading-tight min-h-[3.5rem] md:min-h-[4rem] flex items-center justify-center">
        {stat.text ? stat.text : `${formatNumber(count)}${stat.suffix}`}
      </div>
      <div className="text-sm text-primary-foreground/90 md:text-base leading-normal">
        {stat.label}
      </div>
    </div>
  );
};

export default StatsSection;
